import 'swiper/css'
import 'swiper/css/virtual'
import 'swiper/css/a11y'
import 'swiper/css/keyboard'

import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import { A11y, Controller, Keyboard, Virtual, type Swiper as SwiperRef } from 'swiper'
import { Swiper, SwiperSlide as Slide, useSwiper, useSwiperSlide } from 'swiper/react'

import { FIRST_PAGE, headerHeight, VisuallyHidden } from 'src/helpers'

import { BackButton, NextButton, SliderWrapper, Wrapper } from './Slider.style'
import { useSwipeable } from 'react-swipeable'

export type SliderProps = PropsWithChildren<{
  /**
   * show one page for 'mobile' and two pages and navigation buttons for desktop
   */
  mode?: 'mobile' | 'desktop'
  currentSlide: number
  totalSlides: number
  /**
   * height for each slide in aspect
   * @example `slideHeight: 4` and `slideWidth: 3` for `4:3` aspect ratio
   */
  slideAspectHeight?: number
  /**
   * width for each slide in aspect
   * @example `slideHeight: 4` and `slideWidth: 3` for `4:3` aspect ratio
   */
  slideAspectWidth?: number
  preventClicksPropagation?: boolean
  disableSwipe?: boolean
  onChange?: (slide: number) => void
  onBackClick?: () => void
  onNextClick?: () => void
}>

/**
 * Componente de Slider para as páginas do catálogo
 */
export const Slider = ({
  children,
  currentSlide,
  totalSlides,
  mode = 'mobile',
  slideAspectHeight = 4,
  slideAspectWidth = 3,
  preventClicksPropagation = true,
  disableSwipe,
  onChange,
  onBackClick,
  onNextClick,
}: SliderProps) => {
  const [initialized, setInitialized] = useState(false)
  const [controlledSwiper, setControlledSwiper] = useState<SwiperRef | null>(null)
  const isDesktop = mode === 'desktop'

  const aspectRatio = useMemo(
    () => `${slideAspectWidth * (isDesktop ? 2 : 1)}/${slideAspectHeight}`,
    [isDesktop, slideAspectHeight, slideAspectWidth],
  )

  const visibleSlides = isDesktop ? 2 : 1

  const shouldDisableBackButton = currentSlide <= FIRST_PAGE

  const shouldDisableNextButton = currentSlide >= totalSlides - visibleSlides

  const handleBackClick = () => {
    onBackClick?.()
    controlledSwiper?.slidePrev()
  }

  const handleNextClick = () => {
    onNextClick?.()
    controlledSwiper?.slideNext()
  }

  const handleSlideChange = useCallback(
    ({ activeIndex }: SwiperRef): void => {
      if (!onChange) return

      if (activeIndex <= FIRST_PAGE) onChange(FIRST_PAGE)

      if (activeIndex > totalSlides - visibleSlides) onChange(totalSlides - visibleSlides)

      onChange(activeIndex)
    },
    [onChange, totalSlides, visibleSlides],
  )

  useEffect(() => {
    if (controlledSwiper && controlledSwiper.activeIndex !== currentSlide) {
      controlledSwiper.slideTo(currentSlide)
    }
  }, [controlledSwiper, currentSlide])

  return (
    <Wrapper
      direction="row"
      justify="center"
    >
      <VisuallyHidden
        aria-live="polite"
        aria-atomic={true}
      >
        Página {currentSlide} de {totalSlides}
      </VisuallyHidden>

      {isDesktop && (
        <BackButton
          onClick={handleBackClick}
          disabled={shouldDisableBackButton}
        />
      )}

      <SliderWrapper
        css={{
          width: initialized ? 'fit-content' : '100%',
        }}
      >
        <Swiper
          modules={[Controller, Virtual, A11y, Keyboard]}
          a11y={{ enabled: true }}
          allowTouchMove={!isDesktop}
          aria-label="catálogo digital"
          direction="horizontal"
          draggable={!isDesktop}
          initialSlide={FIRST_PAGE}
          keyboard={{ enabled: isDesktop }}
          noSwiping={disableSwipe ?? isDesktop}
          noSwipingClass="swiper-slide"
          onInit={() => setInitialized(true)}
          onSlideChange={handleSlideChange}
          onSwiper={setControlledSwiper}
          preventClicksPropagation={preventClicksPropagation}
          resistance={false}
          slidesPerGroupSkip={1}
          slidesPerGroup={visibleSlides}
          slidesPerView={visibleSlides}
          tag="ol"
          touchEventsTarget="wrapper"
          virtual
          style={{
            aspectRatio,
            maxHeight: `calc(100vh - ${isDesktop ? headerHeight.desktop : headerHeight.mobile}px)`,
            width: '100%',
          }}
        >
          {children}
          {isDesktop
            ? Array.from({ length: 2 }).map((_, fakeSlideIndex) => (
                <Slide
                  key={`fake-empty-slide-${fakeSlideIndex + 1}`}
                  virtualIndex={totalSlides + fakeSlideIndex + 1}
                >
                  <div />
                </Slide>
              ))
            : null}
        </Swiper>
      </SliderWrapper>

      {isDesktop && (
        <NextButton
          onClick={handleNextClick}
          disabled={shouldDisableNextButton}
        />
      )}
    </Wrapper>
  )
}

export { Slide, useSwiperSlide as useSlide, useSwiper as useSlider }
